import { useEffect, useState } from 'react';
import { Admin } from '../../api.js';
import { FormField } from '../../components/FormField.jsx';
import { PageHeader } from '../../components/PageHeader.jsx';
import { Modal, ConfirmDialog, submitForm } from '../../components/Modal.jsx';
import { EmptyState } from './Teams.jsx';
import { useAuth } from '../../AuthContext.jsx';

const empty = { subject: '', html: '', previewText: '', listIds: [] };

const statusChip = { draft: 'chip-neutral', sending: 'chip-warn', sent: 'chip-success', failed: 'chip-danger' };

export default function Campaigns() {
  const { user } = useAuth();
  const [campaigns, setCampaigns] = useState([]);
  const [lists, setLists] = useState([]);
  const [modal, setModal] = useState(false);
  const [sending, setSending] = useState(null);
  const [confirm, setConfirm] = useState(null);
  const [form, setForm] = useState(empty);
  const [busy, setBusy] = useState(false);
  const [toast, setToast] = useState(null);
  const [optedInCount, setOptedInCount] = useState(null);

  useEffect(() => {
    Admin.lists.list().then((r) => setLists(r.lists)).catch(() => {});
  }, []);

  useEffect(() => {
    if (!sending) { setOptedInCount(null); return; }
    const listIds = sending.listIds || [];
    if (listIds.length) {
      Promise.all(listIds.map((id) => Admin.lists.members(id)))
        .then((results) => {
          const seen = new Set();
          results.forEach((r) => r.contacts.forEach((c) => { if (c.marketingOptIn) seen.add(c.email); }));
          setOptedInCount(seen.size);
        })
        .catch(() => setOptedInCount(null));
    } else {
      Admin.contacts.list({ optIn: 'true' })
        .then((r) => setOptedInCount(r.contacts.length))
        .catch(() => setOptedInCount(null));
    }
  }, [sending]);

  const toggleList = (id) => setForm((f) => ({
    ...f,
    listIds: f.listIds.includes(id) ? f.listIds.filter((x) => x !== id) : [...f.listIds, id],
  }));

  async function refresh() {
    const r = await Admin.campaigns.list();
    setCampaigns(r.campaigns);
  }
  useEffect(() => { refresh(); }, []);

  async function createDraft(e) {
    e.preventDefault();
    setBusy(true);
    try {
      await Admin.campaigns.create({
        subject: form.subject,
        html: form.html,
        previewText: form.previewText || undefined,
        listIds: form.listIds,
      });
      setForm(empty); setModal(false);
      await refresh();
      setToast('Draft saved.');
    } catch (e) {
      setToast(e.message);
    } finally { setBusy(false); setTimeout(() => setToast(null), 2500); }
  }

  async function doSend() {
    setBusy(true);
    try {
      const r = await Admin.campaigns.send(sending.id);
      setToast(`Sent to ${r.sent} · ${r.failed} failed.`);
      setSending(null);
      await refresh();
    } catch (e) {
      setToast(e.data?.message || e.message);
    } finally { setBusy(false); setTimeout(() => setToast(null), 5000); }
  }

  async function sendTest(c) {
    setBusy(true);
    try {
      const r = await Admin.campaigns.sendTest(c.id);
      setToast(`Test sent to ${r.to}.`);
    } catch (e) {
      setToast(e.message);
    } finally { setBusy(false); setTimeout(() => setToast(null), 4000); }
  }

  async function remove(id) { await Admin.campaigns.remove(id); await refresh(); }

  return (
    <div>
      <PageHeader
        eyebrow="Email"
        title="Campaigns"
        description="Only contacts who opted in receive these. Unsubscribe footer and headers are added automatically."
        action={<button onClick={() => setModal(true)} className="btn-primary btn-sm">+ New campaign</button>}
      />

      {toast && (
        <div className="fixed bottom-6 left-1/2 -translate-x-1/2 bg-ink text-canvas px-4 py-2.5 rounded-full text-sm shadow-modal z-50">
          {toast}
        </div>
      )}

      {campaigns.length === 0 ? (
        <EmptyState onAction={() => setModal(true)} label="Compose a campaign" />
      ) : (
        <div className="space-y-3">
          {campaigns.map((c) => (
            <article key={c.id} className="card">
              <div className="flex items-start justify-between gap-4 flex-wrap">
                <div className="min-w-0 flex-1">
                  <div className="flex items-center gap-2 mb-1 flex-wrap">
                    <span className={statusChip[c.status] || 'chip-neutral'}>{c.status}</span>
                    {c.listIds?.length ? (
                      <span className="chip-neutral">
                        {c.listIds.length} list{c.listIds.length === 1 ? '' : 's'}
                      </span>
                    ) : (
                      <span className="chip-neutral">All opted-in</span>
                    )}
                  </div>
                  <h3 className="font-display text-xl mt-1">{c.subject}</h3>
                  <div className="text-xs text-ink-3 mt-2">
                    {c.recipientCount || 0} recipients
                    {c.sentAt ? ` · sent ${new Date(c.sentAt).toLocaleString()}` : ''}
                  </div>
                </div>
                <div className="flex gap-2">
                  {c.status === 'draft' && (
                    <>
                      <button className="btn-outline btn-sm" onClick={() => sendTest(c)} disabled={busy}>
                        Send test to me
                      </button>
                      <button className="btn-primary btn-sm" onClick={() => setSending(c)}>
                        Send
                      </button>
                    </>
                  )}
                  <button className="btn-ghost btn-sm text-danger" onClick={() => setConfirm(c)}>Delete</button>
                </div>
              </div>
            </article>
          ))}
        </div>
      )}

      <Modal
        open={modal}
        onClose={() => setModal(false)}
        eyebrow="New"
        title="Compose campaign"
        size="lg"
        footer={
          <>
            <button onClick={() => setModal(false)} className="btn-ghost">Cancel</button>
            <button type="button" onClick={() => submitForm('campaign-form')} className="btn-primary" disabled={busy}>
              {busy ? 'Saving…' : 'Save draft'}
            </button>
          </>
        }
      >
        <form id="campaign-form" onSubmit={createDraft} className="space-y-4">
          <FormField label="Subject" required>
            <input className="input" value={form.subject} onChange={(e) => setForm({ ...form, subject: e.target.value })} required autoFocus />
          </FormField>
          <FormField label="Preview text" hint="Optional — shown after the subject in inbox previews">
            <input className="input" value={form.previewText} onChange={(e) => setForm({ ...form, previewText: e.target.value })} />
          </FormField>
          <FormField label="HTML body" required>
            <textarea
              className="input font-mono text-xs leading-relaxed"
              rows={12}
              value={form.html}
              onChange={(e) => setForm({ ...form, html: e.target.value })}
              required
              placeholder="<p>Hi team,</p>"
            />
          </FormField>
          <div>
            <div className="label">Target lists <span className="text-ink-3 font-normal">— leave empty for all opted-in contacts</span></div>
            <div className="flex flex-wrap gap-2">
              {lists.map((l) => {
                const on = form.listIds.includes(l.id);
                return (
                  <button key={l.id} type="button" onClick={() => toggleList(l.id)}
                    className={`chip ${on ? 'bg-brand text-white' : 'bg-line-soft text-ink-2 hover:bg-line'}`}>
                    {l.name}<span className="opacity-70 ml-1">· {l.contactCount || 0}</span>
                  </button>
                );
              })}
              {!lists.length && <span className="text-xs text-ink-3">No lists defined. <a href="/admin/lists" className="underline">Create one →</a></span>}
            </div>
          </div>
          <div className="text-[11px] text-ink-3 border-t border-line pt-3 space-y-1">
            <p>After saving, use <strong>Send test to me</strong> to preview, then <strong>Send</strong> to the list.</p>
            <p>Unsubscribe footer and <code>List-Unsubscribe</code> headers are added automatically.</p>
          </div>
        </form>
      </Modal>

      <ConfirmDialog
        open={!!sending}
        onClose={() => setSending(null)}
        onConfirm={doSend}
        title={`Send "${sending?.subject}"?`}
        message={
          optedInCount == null
            ? 'Counting opted-in recipients…'
            : optedInCount === 0
              ? 'No opted-in contacts match this campaign. Add people to the selected list(s) or pick different lists.'
              : `Sends to ${optedInCount} opted-in contact${optedInCount === 1 ? '' : 's'}${sending?.listIds?.length ? ` across ${sending.listIds.length} list${sending.listIds.length === 1 ? '' : 's'}` : ''}. You cannot unsend. Send a test to ${user?.email} first if you haven't already.`
        }
        confirmLabel="Send"
        danger
      />
      <ConfirmDialog
        open={!!confirm}
        onClose={() => setConfirm(null)}
        onConfirm={() => remove(confirm.id)}
        title="Delete campaign?"
        message="This removes the draft. Sent campaigns' delivery logs remain."
        confirmLabel="Delete"
        danger
      />
    </div>
  );
}
